import { combineReducers } from 'redux'

import {
  FinalTripartita,
  InitialTripartita,
  IntermediateTripartita,
}                           from '../../api'
import { api }              from '../../client'
import { bind }             from '../../utils'
import createAction         from '../redux-utils/create-action'
import { Reducer }          from '../types'
import { createRequest }    from './communication.duck'
import { ApplicationState } from './index'

export interface TripartitaPackage {
  initial?: InitialTripartita
  intermediate?: IntermediateTripartita
  final?: FinalTripartita
}

const NAMESPACE = 'tripartita'

export const TRIPARTITA_GET_BY_PROCESS_ID_SUCCESS = `[${NAMESPACE}] BY_PROCESS_ID_GET_SUCCESS`
export const TRIPARTITA_INITIAL_UPDATED = `[${NAMESPACE}] INITIAL_UPDATED`
export const TRIPARTITA_INTERMEDIATE_UPDATED = `[${NAMESPACE}] INTERMEDIATE_UPDATE`
export const TRIPARTITA_FINAL_UPDATED = `[${NAMESPACE}] FINAL_UPDATED`

type ExtendedTripartita =
  { type: string }
  & (
  InitialTripartita
  | IntermediateTripartita
  | FinalTripartita
  )

interface ById {
  [ id: string ]: ExtendedTripartita
}

const byId: Reducer<ById> = (
  state = {},
  {
    type,
    payload,
  },
) => {
  switch (type) {
    case TRIPARTITA_GET_BY_PROCESS_ID_SUCCESS: {
      const { tripartitaPackage } = payload

      const tripartitasObject =
        Object.keys(tripartitaPackage)
              .reduce(
                (reduction, next) => {
                  const tripartita = tripartitaPackage[ next ]

                  return {
                    ...reduction,
                    [ tripartita.id + next ]: { type: next, ...tripartita },
                  }
                },
                {},
              )

      return {
        ...state,
        ...tripartitasObject,
      }
    }

    case TRIPARTITA_INITIAL_UPDATED:
    case TRIPARTITA_INTERMEDIATE_UPDATED:
    case TRIPARTITA_FINAL_UPDATED: {
      const { tripartitaId, tripartita, type } = payload

      return {
        ...state,
        [ tripartitaId + type ]: { ...tripartita, type },
      }
    }

    default:
      return state
  }
}

interface ByProcessId {
  [ processId: number ]: string []
}

const byProcessId: Reducer<ByProcessId> = (
  state = [],
  {
    type,
    payload,
  },
) => {
  switch (type) {
    case TRIPARTITA_GET_BY_PROCESS_ID_SUCCESS: {
      const { processId, tripartitaPackage } = payload

      return {
        ...state,
        [ processId ]: Object
                         .keys(tripartitaPackage)
                         .map(
                           tripartitaType => tripartitaPackage[ tripartitaType ].id + tripartitaType
                         ),
      }
    }

    default:
      return state
  }
}

export default combineReducers({
  byProcessId,
  byId,
})

export interface TripartitaState {
  byProcessId: ByProcessId
  byId: ById
}

const handleTripartitaGetResponse = (res, processId) =>
  createAction(
    TRIPARTITA_GET_BY_PROCESS_ID_SUCCESS,
    {
      processId,
      tripartitaPackage: res,
    },
  )

const handleTripartitaInitialUpdated = (tripartita, tripartitaId) =>
  createAction(
    TRIPARTITA_INITIAL_UPDATED,
    {
      tripartitaId,
      tripartita,
      type: 'initial',
    },
  )

const handleTripartitaIntermediateUpdated = (tripartita, tripartitaId) =>
  createAction(
    TRIPARTITA_INTERMEDIATE_UPDATED,
    {
      tripartitaId,
      tripartita,
      type: 'intermediate',
    }
  )

const handleTripartitaFinalUpdated = (tripartita, tripartitaId) =>
  createAction(
    TRIPARTITA_FINAL_UPDATED,
    {
      tripartitaId,
      tripartita,
      type: 'final',
    }
  )

export const tripartitaGetByProcessIdRequest = createRequest({
  namespace:      'tripartita-get',
  request:        bind(api)(api.listTripartita),
  successHandler: handleTripartitaGetResponse,
})

export const tripartitaUpdateInitialAsCoachRequest = createRequest({
  namespace:      'tripartita-update-initial',
  request:        bind(api)(api.updateInitialTripartitaCoach),
  successHandler: handleTripartitaInitialUpdated,
})

export const tripartitaUpdateInitialAsCoacheeRequest = createRequest({
  namespace:      'tripartita-update-initial',
  request:        bind(api)(api.updateInitialTripartitaCoachee),
  successHandler: handleTripartitaInitialUpdated,
})

export const tripartitaUpdateIntermediateRequest = createRequest({
  namespace:      'tripartita-update-intermediate',
  request:        bind(api)(api.updateIntermediateTripartita),
  successHandler: handleTripartitaIntermediateUpdated,
})

export const tripartitaUpdateFinalRequest = createRequest({
  namespace:      'tripartita-update-final',
  request:        bind(api)(api.updateFinalTripartita),
  successHandler: handleTripartitaFinalUpdated,
})

export const selectTripartitaByProcessId =
  (processId: number) =>
    ({ tripartita: { byProcessId, byId } }: ApplicationState): TripartitaPackage => {
      const tripartitaIds = byProcessId[ processId ]

      if (!tripartitaIds) return {}

      return tripartitaIds.map(id => byId[ id ]).reduce(
        (reduction, next) => ({
          ...reduction,
          [ next.type ]: next,
        }),
        {},
      )
    }



// WEBPACK FOOTER //
// ./src/redux/ducks/tripartita.duck.ts